import { supabase } from '@/lib/supabaseClient';

export interface PopularTemplate {
  id: string;
  name: string;
  platform: string;
  category: string | null;
  price: number;
  rating: number;
  reviews_count: number;
  downloads_count: number;
  thumbnail_url: string | null;
  tags: string[];
  popularity_score: number;
}

interface PopularityCache {
  data: PopularTemplate[];
  timestamp: number;
}

const POPULARITY_CACHE_TTL = 10 * 60 * 1000; // 10 minutes
let popularityCache: PopularityCache | null = null;

/**
 * Compute a popularity score for a template.
 * Weights: downloads (40%), rating (30%), reviews (20%), price-adjusted (10%)
 */
export function computePopularityScore(template: {
  downloads_count: number;
  rating: number;
  reviews_count: number;
  price: number;
}): number {
  const downloadScore = Math.min((template.downloads_count || 0) / 100, 100) * 0.4;
  const ratingScore = ((template.rating || 0) / 5) * 100 * 0.3;
  const reviewScore = Math.min((template.reviews_count || 0) / 10, 100) * 0.2;
  const priceScore = Math.min((template.price || 0) / 150, 1) * 100 * 0.1;
  return downloadScore + ratingScore + reviewScore + priceScore;
}

export async function fetchPopularTemplates(limit = 6): Promise<PopularTemplate[]> {
  const now = Date.now();

  // Return cached data if fresh
  if (popularityCache && now - popularityCache.timestamp < POPULARITY_CACHE_TTL) {
    return popularityCache.data.slice(0, limit);
  }

  try {
    const { data, error } = await supabase
      .from('templates')
      .select('id, name, platform, category, price, rating, reviews_count, downloads_count, thumbnail_url, tags')
      .eq('is_published', true)
      .limit(50); // Fetch top 50 to re-rank by computed score

    if (error) {
      console.error('[Popularity] fetch error:', error);
      return [];
    }

    const ranked: PopularTemplate[] = (data || []).map((t: any) => ({
      ...t,
      downloads_count: t.downloads_count ?? 0,
      popularity_score: computePopularityScore(t),
    }));

    // Sort by computed popularity score descending
    ranked.sort((a, b) => b.popularity_score - a.popularity_score);

    popularityCache = { data: ranked, timestamp: now };
    return ranked.slice(0, limit);
  } catch (err) {
    console.error('[Popularity] unexpected error:', err);
    return [];
  }
}

export function invalidatePopularityCache() {
  popularityCache = null;
}
